import React, { useState, useCallback, useEffect, memo, useMemo, useRef } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { useSelector } from 'react-redux';
import AutoCompleteField from '../../../components/molecules/AutoCompleteField';
import { AutoCompleteSizes } from '../../../components/molecules/AdvancedAutoComplete/consts';
import { AutoCompleteOptionType } from '../../../components/molecules/autocompleteTypes';
import Fields from '../../../services/fields';
import Reports from '../../../services/reports';
import Button from '../../../components/atoms/Button';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import { useUrlParams } from '../../../utils/useUrlParamsHook';
import Datepicker from '../../../components/widgets/Datepicker';
import css from './Filters.css';
import {
    TIME_OPTIONS,
    DIMENSIONS_OPTIONS,
    FILTERS_OPTIONS,
    Dimensions,
    TimeSelectors,
    TimePeriods,
    MetricsConfig,
    MetricType,
} from '../../consts';
import { isObjectEmpty } from '../../../utils/general';
import { useAsync } from '../../../utils/customHooks';
import { createCohortOptions } from '../../../creativeGallery/metricsUtils';
import fieldVisibilityRules from '../../../selectors/reportsConfig/fieldVisibilityRules';
import { getAdminModeEnabled, getUserData } from '../../../selectors/user';

const reportsService = new Reports();

const toOption = key => ({ value: key, label: `STATIC.${key}` });

const getFilteredMetricsOptions = availableCohortOptionsSet => {
    const entries = Object.entries(MetricsConfig).filter(
        ([, { apiType, apiName, period }]) =>
            apiType !== 'cohort' || availableCohortOptionsSet.has(`${apiName}_${period}`)
    );

    return entries.reduce(
        (acc, [key, { types }]) => {
            if (types.includes(MetricType.COLOR)) acc.color.push(toOption(key));
            if (types.includes(MetricType.SIZE)) acc.size.push(toOption(key));
            return acc;
        },
        { color: [], size: [] }
    );
};

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANOMALY_ANALYSIS.FILTERS';
const MAX_DIMENSIONS = 3;
const DATE_FORMAT = 'YYYY-MM-DD';
const DAYS = 'days';

// Maps each time period key to its dateLimit shape, e.g. { DAY_BY_DAY: { days: 1 }, WEEK_BY_WEEK: { days: 7 }, MONTH_BY_MONTH: { months: 1 } }
const DATE_LIMITS = Object.fromEntries(
    Object.entries(TimePeriods).map(([key, { amount, unit }]) => [key, { [unit]: amount }])
);

const getPeriodRange = (baseDate, amount, unit) => {
    const periodEnd = moment(baseDate);
    const periodStart = moment(periodEnd)
        .subtract(amount, unit)
        .add(1, DAYS);
    return { start: periodStart.format(DATE_FORMAT), end: periodEnd.format(DATE_FORMAT) };
};

const getDefaultDates = timeOption => {
    const today = moment();
    const { amount, unit } = TimePeriods[timeOption.value] || TimePeriods.DAY_BY_DAY;

    const firstPeriod = getPeriodRange(today, amount, unit);
    const secondPeriod = getPeriodRange(moment(firstPeriod.start).subtract(1, DAYS), amount, unit);

    return { first: firstPeriod, second: secondPeriod };
};

const getAvailableOptions = (options, selectedValues, currentValue) => {
    const selectedSet = new Set(selectedValues.filter(v => v !== currentValue));
    return options.filter(option => !selectedSet.has(option.value));
};

const fieldsService = new Fields();

const Section = memo(({ labelKey, children, addButton, errorKey }) => (
    <div className={css.section}>
        <div className={css.sectionLabel}>
            <Translate id={labelKey} />
        </div>
        {errorKey ? (
            <div className={css.error}>
                <Translate id={errorKey} />
            </div>
        ) : (
            <>
                {children}
                {addButton && (
                    <Button
                        type="link"
                        Icon="plus"
                        iconVariant={IconVariants.ACTIVE}
                        iconSize={IconSizes.X_SMALL}
                        className={css.addButton}
                        {...addButton}
                    />
                )}
            </>
        )}
    </div>
));

Section.propTypes = {
    labelKey: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    addButton: PropTypes.shape({
        text: PropTypes.string.isRequired,
        onClick: PropTypes.func.isRequired,
        disabled: PropTypes.bool,
        tooltipKey: PropTypes.string,
    }),
    errorKey: PropTypes.string,
};

const FilterAutoComplete = memo(
    ({ options, value, onChange, label, clearable = false, loading = false, onRemove, isMulti = false }) => (
        <AutoCompleteField
            options={options}
            value={isMulti ? value : value ? [value] : []}
            onChange={onChange}
            label={label}
            advanced
            isMulti={isMulti}
            containerClass={css.filterAutocomplete}
            size={AutoCompleteSizes.Small}
            clearable={clearable}
            loading={loading}
            ignoreScrollEvents
            onRemove={onRemove}
            removable={!!onRemove}
        />
    )
);

FilterAutoComplete.propTypes = {
    options: PropTypes.arrayOf(AutoCompleteOptionType).isRequired,
    value: AutoCompleteOptionType,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    clearable: PropTypes.bool,
    loading: PropTypes.bool,
    onRemove: PropTypes.func,
    isMulti: PropTypes.bool,
};

const UP = 'up';
const DOWN = 'down';

const ReorderButton = ({ disabled, type, dimension, setDimensions, onReorderComplete = () => {} }) => {
    const offset = type === UP ? -1 : 1;

    const moveDimension = useCallback(() => {
        setDimensions(prev => {
            const index = prev.findIndex(d => d.value === dimension.value);
            const swapIndex = index + offset;
            if (swapIndex < 0 || swapIndex >= prev.length) return prev;

            const next = [...prev];
            [next[swapIndex], next[index]] = [next[index], next[swapIndex]];
            return next;
        });
        onReorderComplete();
    }, [dimension.value, offset, setDimensions, onReorderComplete]);

    return (
        <Icon
            name={`chevron-${type}`}
            size={IconSizes.X_SMALL}
            variant={IconVariants.ACTIVE}
            onClick={moveDimension}
            disabled={disabled}
        />
    );
};

ReorderButton.propTypes = {
    disabled: PropTypes.bool,
    type: PropTypes.string,
    setDimensions: PropTypes.func,
    onReorderComplete: PropTypes.func,
    dimension: AutoCompleteOptionType,
};

const getSelectedOption = (options, value) => options.find(option => option.value === value);

const updateSelectedByUrlCallback = (prev, options, value) => {
    if (!value) return prev;

    const urlValue = getSelectedOption(options, value);
    if (!urlValue) return prev;
    return prev && urlValue.value === prev.value ? prev : urlValue;
};

const resolveOptionsFromUrl = (urlValues, options) => {
    if (!urlValues) return null;
    return urlValues.map(value => getSelectedOption(options, value)).filter(Boolean);
};

const DEFAULT_TIME_SELECTOR = TimeSelectors.WEEK_BY_WEEK;
const DEFAULT_DIMENSIONS = [Dimensions.APP, Dimensions.SOURCE];
const DEFAULT_DATES = getDefaultDates(getSelectedOption(TIME_OPTIONS, DEFAULT_TIME_SELECTOR));

const Filters = ({ onRun = () => {} }) => {
    const { getParamsObject, setParamsObject } = useUrlParams();
    const [timeSelector, setTimeSelector] = useState(getSelectedOption(TIME_OPTIONS, DEFAULT_TIME_SELECTOR));
    const [dimensions, setDimensions] = useState(
        DEFAULT_DIMENSIONS.map(value => getSelectedOption(DIMENSIONS_OPTIONS, value))
    );
    const [colorMetric, setColorMetric] = useState(null);
    const [sizeMetric, setSizeMetric] = useState(null);
    const [filters, setFilters] = useState([]);
    const [filterFieldOptions, setFilterFieldOptions] = useState({});
    const [filterFieldsLoading, setFilterFieldsLoading] = useState(false);
    const [filterFieldsError, setFilterFieldsError] = useState(false);

    const [firstPeriod, setFirstPeriod] = useState(DEFAULT_DATES.first);
    const [secondPeriod, setSecondPeriod] = useState(DEFAULT_DATES.second);
    const [shouldRunQuery, setShouldRunQuery] = useState(false);
    const urlLoadedRef = useRef(false);

    const isSingleDay = timeSelector.value === TimeSelectors.DAY_BY_DAY;
    const isMaxDimensions = dimensions.length >= MAX_DIMENSIONS;
    const isRunDisabled =
        !colorMetric ||
        !sizeMetric ||
        !firstPeriod.start ||
        !firstPeriod.end ||
        !secondPeriod.start ||
        !secondPeriod.end;

    const isAdmin = useSelector(getAdminModeEnabled);
    const userData = useSelector(state => getUserData(state));

    const fieldsRule = useCallback(field => fieldVisibilityRules.reports(field, isAdmin, userData), [
        isAdmin,
        userData,
    ]);

    const [{ data: cohortData }, fetchCohortData] = useAsync();

    useEffect(() => {
        fetchCohortData(reportsService.getCohortDataAndConversionEvents());
    }, [fetchCohortData]);

    const { color: COLOR_METRICS_OPTIONS, size: SIZE_METRICS_OPTIONS } = useMemo(() => {
        if (!cohortData) return { color: [], size: [] };

        const cohortMetrics = cohortData?.cohortMetrics.filter(fieldsRule);
        const cohortPeriods = cohortData?.periods.filter(fieldsRule);
        const availableSet = new Set(createCohortOptions(cohortMetrics, cohortPeriods).map(({ value }) => value));
        return getFilteredMetricsOptions(availableSet);
    }, [cohortData, fieldsRule]);

    useEffect(() => {
        if (urlLoadedRef.current || !COLOR_METRICS_OPTIONS.length) return;

        const url = getParamsObject();

        urlLoadedRef.current = true;

        if (!url || isObjectEmpty(url)) return;

        setTimeSelector(prev => updateSelectedByUrlCallback(prev, TIME_OPTIONS, url.timeSelector));

        const resolvedDimensions = resolveOptionsFromUrl(url.dimensions, DIMENSIONS_OPTIONS);
        if (resolvedDimensions?.length) setDimensions(resolvedDimensions);

        setColorMetric(prev => updateSelectedByUrlCallback(prev, COLOR_METRICS_OPTIONS, url.colorMetric));
        setSizeMetric(prev => updateSelectedByUrlCallback(prev, SIZE_METRICS_OPTIONS, url.sizeMetric));

        if (url.filters) {
            const resolvedFilters = resolveOptionsFromUrl(
                url.filters.map(f => f.type),
                FILTERS_OPTIONS
            );
            setFilters(
                resolvedFilters.map(urlFilter => ({
                    type: urlFilter.value,
                    values: url.filters.find(f => f.type === urlFilter.value)?.values || [],
                }))
            );
        }

        if (url.firstPeriod) setFirstPeriod(url.firstPeriod);
        if (url.secondPeriod) setSecondPeriod(url.secondPeriod);

        if (url.colorMetric && url.sizeMetric) setShouldRunQuery(true);
    }, [COLOR_METRICS_OPTIONS]);

    useEffect(() => {
        setFilterFieldsLoading(true);
        fieldsService
            .updateFilterFields()
            .then(response => {
                const fields = response.value || [];
                const optionsMap = FILTERS_OPTIONS.reduce((acc, { value, apiName }) => {
                    const field = fields.find(f => f.name === apiName);
                    acc[value] = (field?.values || []).map(fieldValue => ({
                        name: fieldValue.name,
                        display_name: fieldValue.display_name || fieldValue.name,
                    }));
                    return acc;
                }, {});
                setFilterFieldOptions(optionsMap);
            })
            .catch(() => {
                setFilterFieldsError(true);
            })
            .finally(() => setFilterFieldsLoading(false));
    }, []);

    const getFilterAutoCompleteOptions = useCallback(
        filterType => {
            const fields = filterFieldOptions[filterType] || [];
            return fields.map(option => ({ label: option.display_name, value: option.name }));
        },
        [filterFieldOptions]
    );

    const handleTimeSelectorChange = useCallback(value => {
        if (!value) return;

        setTimeSelector(value);
        const dates = getDefaultDates(value);
        setFirstPeriod(dates.first);
        setSecondPeriod(dates.second);
    }, []);

    const handlePeriodChange = useCallback(
        (setter, data) => {
            const { amount, unit } = TimePeriods[timeSelector.value];
            const start = moment(data.start_date);
            const end = moment(data.end_date);
            const minEnd = start.clone().add(amount - 1, unit);
            setter({
                start: start.format(DATE_FORMAT),
                end: end.isBefore(minEnd) ? minEnd.format(DATE_FORMAT) : end.format(DATE_FORMAT),
            });
        },
        [timeSelector]
    );

    const handleDimensionChange = useCallback((value, currentValue) => {
        if (!value) return;
        setDimensions(prev => prev.map(d => (d.value === currentValue ? value : d)));
    }, []);

    const handleAddDimension = useCallback(() => {
        setDimensions(prev => {
            if (prev.length >= MAX_DIMENSIONS) return prev;
            return [
                ...prev,
                getAvailableOptions(
                    DIMENSIONS_OPTIONS,
                    prev.map(d => d.value),
                    null
                )[0],
            ];
        });
    }, []);

    const handleRemoveDimension = useCallback(value => {
        setDimensions(prev => prev.filter(d => d.value !== value));
    }, []);

    const handleAddFilter = useCallback(() => {
        setFilters(prev => {
            const option = getAvailableOptions(
                FILTERS_OPTIONS,
                prev.map(f => f.type),
                null
            )[0];
            return [...prev, { type: option.value, values: [] }];
        });
    }, []);

    const handleFilterChange = useCallback((value, currentType) => {
        if (!value) return;
        setFilters(prev => prev.map(f => (f.type === currentType ? { type: value.value, values: [] } : f)));
    }, []);

    const handleRemoveFilter = useCallback(type => {
        setFilters(prev => prev.filter(f => f.type !== type));
    }, []);

    const handleFilterValueChange = useCallback((type, values) => {
        setFilters(prev => prev.map(f => (f.type === type ? { ...f, values: values || [] } : f)));
    }, []);

    const handleRun = useCallback(() => {
        setShouldRunQuery(true);
    }, []);

    const addDimensionButton = useMemo(
        () => ({
            text: `${TRANSLATION_PREFIX}.ADD_DIMENSIONS`,
            onClick: handleAddDimension,
            disabled: isMaxDimensions,
            tooltipKey: isMaxDimensions ? `${TRANSLATION_PREFIX}.MAX_DIMENSIONS_TOOLTIP` : undefined,
        }),
        [handleAddDimension, isMaxDimensions]
    );

    const addFilterButton = useMemo(
        () => ({
            text: 'STATIC.FILTERS_SECTION.ADD_FILTER',
            onClick: handleAddFilter,
            disabled: filters.length >= FILTERS_OPTIONS.length,
        }),
        [handleAddFilter, filters.length]
    );

    useEffect(() => {
        if (!shouldRunQuery) return;

        setShouldRunQuery(false);
        if (isRunDisabled) return;
        const snapshot = { timeSelector, dimensions, colorMetric, sizeMetric, filters, firstPeriod, secondPeriod };
        setParamsObject({
            timeSelector: timeSelector.value,
            dimensions: dimensions.map(d => d.value),
            colorMetric: colorMetric?.value,
            sizeMetric: sizeMetric?.value,
            filters,
            firstPeriod,
            secondPeriod,
        });
        onRun(snapshot);
    }, [shouldRunQuery]);

    return (
        <div className={css.filters}>
            <div className={css.title}>
                <Translate id={`${TRANSLATION_PREFIX}.TITLE`} />
            </div>
            <Section labelKey={`${TRANSLATION_PREFIX}.TIME_SELECTOR`}>
                <FilterAutoComplete options={TIME_OPTIONS} value={timeSelector} onChange={handleTimeSelectorChange} />
                <Datepicker
                    id="anomaly-first-period"
                    labelKey={`${TRANSLATION_PREFIX}.FIRST_PERIOD`}
                    startDate={firstPeriod.start}
                    endDate={firstPeriod.end}
                    onApply={data => handlePeriodChange(setFirstPeriod, data)}
                    singleDatePicker={isSingleDay}
                    dateLimit={DATE_LIMITS[timeSelector.value]}
                    ranges={{}}
                    containerClass={css.datepicker}
                    appendToBody
                />
                <Datepicker
                    id="anomaly-second-period"
                    labelKey={`${TRANSLATION_PREFIX}.SECOND_PERIOD`}
                    startDate={secondPeriod.start}
                    endDate={secondPeriod.end}
                    onApply={data => handlePeriodChange(setSecondPeriod, data)}
                    singleDatePicker={isSingleDay}
                    dateLimit={DATE_LIMITS[timeSelector.value]}
                    ranges={{}}
                    containerClass={css.datepicker}
                    appendToBody
                />
            </Section>
            <Section labelKey="STATIC.DIMENSIONS" addButton={addDimensionButton}>
                {dimensions.map((dimension, index) => (
                    <div key={index} className={css.dimensionRow}>
                        <FilterAutoComplete
                            options={getAvailableOptions(
                                DIMENSIONS_OPTIONS,
                                dimensions.map(d => d.value),
                                dimension.value
                            )}
                            value={dimension}
                            onChange={value => handleDimensionChange(value, dimension.value)}
                            onRemove={dimensions.length > 1 ? () => handleRemoveDimension(dimension.value) : undefined}
                        />
                        {dimensions.length > 1 && (
                            <>
                                {[UP, DOWN].map(type => (
                                    <ReorderButton
                                        key={type}
                                        type={type}
                                        dimension={dimension}
                                        setDimensions={setDimensions}
                                        onReorderComplete={handleRun}
                                        disabled={type === UP ? !index : index === dimensions.length - 1}
                                    />
                                ))}
                            </>
                        )}
                    </div>
                ))}
            </Section>
            <Section labelKey="STATIC.METRICS">
                <FilterAutoComplete
                    label={`${TRANSLATION_PREFIX}.COLOR_METRIC`}
                    options={COLOR_METRICS_OPTIONS}
                    value={colorMetric}
                    onChange={setColorMetric}
                />
                <FilterAutoComplete
                    label={`${TRANSLATION_PREFIX}.SIZE_METRIC`}
                    options={SIZE_METRICS_OPTIONS}
                    value={sizeMetric}
                    onChange={setSizeMetric}
                />
            </Section>
            <Section
                labelKey="STATIC.FILTERS"
                errorKey={filterFieldsError ? `${TRANSLATION_PREFIX}.LOAD_ERROR` : undefined}
                addButton={addFilterButton}
            >
                {filters.map(filter => (
                    <div key={filter.type} className={css.filterBlock}>
                        <FilterAutoComplete
                            options={getAvailableOptions(
                                FILTERS_OPTIONS,
                                filters.map(f => f.type),
                                filter.type
                            )}
                            value={FILTERS_OPTIONS.find(o => o.value === filter.type)}
                            onChange={value => handleFilterChange(value, filter.type)}
                            onRemove={() => handleRemoveFilter(filter.type)}
                        />
                        <FilterAutoComplete
                            options={getFilterAutoCompleteOptions(filter.type)}
                            value={filter.values}
                            onChange={values => handleFilterValueChange(filter.type, values)}
                            label={`${TRANSLATION_PREFIX}.VALUES_PLACEHOLDER`}
                            isMulti
                            loading={filterFieldsLoading}
                        />
                    </div>
                ))}
            </Section>
            <Button
                type="primary"
                text={`${TRANSLATION_PREFIX}.RUN`}
                onClick={handleRun}
                className={css.runButton}
                disabled={isRunDisabled}
            />
        </div>
    );
};

Filters.propTypes = {
    onRun: PropTypes.func,
};

export default memo(Filters);
